import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'mtp-dark-mode';

/**
 * Safely reads from localStorage.
 * @returns {boolean} true if dark mode was previously enabled
 */
const readPreference = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Safely writes to localStorage.
 * @param {boolean} value
 */
const writePreference = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // localStorage not available (e.g., restrictive incognito mode)
  }
};

/**
 * Applies or removes the dark theme attribute on the document root.
 * @param {boolean} isDark
 */
const applyTheme = (isDark) => {
  if (isDark) {
    document.documentElement.setAttribute('data-bs-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-bs-theme');
  }
};

/**
 * Hook that manages dark mode state.
 * Reads from localStorage on init, applies data-bs-theme to <html>,
 * and persists changes.
 *
 * @returns {{ isDark: boolean, toggleDarkMode: () => void }}
 */
export const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    const pref = readPreference();
    applyTheme(pref);
    return pref;
  });

  useEffect(() => {
    applyTheme(isDark);
    writePreference(isDark);
  }, [isDark]);

  const toggleDarkMode = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  return { isDark, toggleDarkMode };
};
